import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { AppTopRightMenu } from "@/components/app-layout/AppTopRightMenu";
import { ExternalFileProviderDisplayName } from "@/components/display/display-name/ExternalFileProviderDisplayName";
import { FileStatus } from "@/components/file/FileStatus";
import { Markdown } from "@/components/markdown/Markdown";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFileData } from "@/data/use-file-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { saveUrlAs } from "@/lib/file";
import {
  GetFileRagSummaryResponseDto,
  fileControllerGetDownloadUrl,
} from "@/lib/gen-api";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faCircle,
  faDownload,
  faRefresh,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

const LEVEL_CLASSNAMES: {
  container: Record<number, string>;
  title: Record<number, string>;
} = {
  container: {
    1: "pl-4 bg-background-1",
    2: "pl-8 bg-background-3",
    3: "pl-12 bg-background-5",
    4: "pl-16 bg-background-7",
    5: "pl-20 bg-background-9",
  },
  title: {
    1: "text-4xl",
    2: "text-3xl",
    3: "text-2xl",
    4: "text-xl",
    5: "text-md",
  },
};

type TabsTriggerValue = "markdown" | "progress" | "nodes";
const LABEL_CLASSNAME = "w-16 font-bold";

type RagSummary = GetFileRagSummaryResponseDto[number] & {
  level: number;
};

export const FileDebugPage = () => {
  const groupUuid = useGroupUuid();
  const kitUuid = useKitUuid();
  const params = useParams({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const folderUuid = params.folderUuid as string;
  const fileUuid = params.fileUuid as string;
  const intervalHandler = useRef<null | NodeJS.Timeout>(null);
  const [activeTab, setActiveTab] = useState<TabsTriggerValue>("progress");

  const { file, progress, markdown, ragSummary, reparse } = useFileData({
    fileUuid,
  });

  const extendedRagSummary: RagSummary[] = useMemo(() => {
    const nodesWithLevel: RagSummary[] = [];
    const uuidLevelMap = new Map<string, number>();

    ragSummary.forEach((node) => {
      const parentLevel = node.fileNode.parentUuid
        ? (uuidLevelMap.get(node.fileNode.parentUuid) ?? 0)
        : 0;
      const currentLevel = parentLevel + 1;
      uuidLevelMap.set(node.fileNode.uuid, currentLevel);
      nodesWithLevel.push({ ...node, level: currentLevel });
    });
    return ragSummary.map((point) => ({
      ...point,
      level: uuidLevelMap.get(point.fileNode.uuid) ?? 0,
    }));
  }, [ragSummary]);

  useEffect(() => {
    return () => {
      if (intervalHandler.current) {
        clearInterval(intervalHandler.current);
      }
    };
  }, []);

  const handleDownloadFile = async () => {
    if (!groupUuid || !kitUuid || !folderUuid || !fileUuid) return;

    const response = await fileControllerGetDownloadUrl({
      path: {
        fileUuid,
      },
    });

    const url = response.data?.url;

    if (!url) {
      toast.error("Failed to get download URL");
      return;
    }

    saveUrlAs(url, file?.name || "download");
  };

  const handleReparse = async () => {
    reparse();
  };

  return (
    <>
      <AppTopRightMenu.ContentPortal display="append">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            {progress?.isParsing ? (
              <Button disabled>
                <FontAwesomeIcon
                  icon={faRefresh}
                  className="animate-[spin_2s_linear_infinite]"
                />{" "}
                Reparsing...
              </Button>
            ) : (
              <Button>
                <FontAwesomeIcon icon={faRefresh} /> Reparse
              </Button>
            )}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>Reparse File</AlertDialogHeader>
            <AlertDialogDescription>
              Are you sure you want to reparse this file?
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReparse}>
                <FontAwesomeIcon icon={faRefresh} /> Reparse
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button onClick={handleDownloadFile}>
          <FontAwesomeIcon icon={faDownload} /> Download
        </Button>
      </AppTopRightMenu.ContentPortal>
      <Page>
        <PageHeader>
          <PageHeader.Title>File Debug</PageHeader.Title>
        </PageHeader>
        <PageBody>
          <div className="flex flex-col gap-8">
            <div className="flex flex-row gap-8">
              <div className="flex w-2/3 flex-col gap-4 text-xs">
                <div className="text-2xl">File</div>
                <div className="flex flex-row items-center gap-2">
                  <div className={LABEL_CLASSNAME}>UUID:</div>
                  <div>{file?.uuid}</div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className={LABEL_CLASSNAME}>Name:</div>
                  <div>{file?.name}</div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className={LABEL_CLASSNAME}>Bucket:</div>
                  <div>{file?.bucket_name}</div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className={LABEL_CLASSNAME}>Path: </div>
                  <div>{file?.path}</div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className={LABEL_CLASSNAME}>MD Path:</div>
                  <div>{file?.markdownPath}</div>
                </div>
                <div className="flex flex-row items-center gap-2">
                  <div className={LABEL_CLASSNAME}>Source:</div>
                  <div>
                    {file?.externalProvider ? (
                      <ExternalFileProviderDisplayName
                        externalProvider={file.externalProvider}
                      />
                    ) : (
                      "Uploaded"
                    )}
                  </div>
                </div>
              </div>
              <div className="flex w-1/3 flex-col gap-4">
                <div className="text-2xl">Status</div>
                <div className="flex flex-row items-center gap-2">
                  Status: {file && <FileStatus file={file} label />}{" "}
                </div>
                <div>Error: {file?.error || "N/A"}</div>
              </div>
            </div>

            <Separator />

            <Tabs
              value={activeTab}
              onValueChange={(newTab) =>
                setActiveTab(newTab as TabsTriggerValue)
              }
            >
              <TabsList className="flex flex-row gap-2 pb-8">
                <TabsTrigger value="progress">Progress</TabsTrigger>
                <TabsTrigger value="markdown">Markdown</TabsTrigger>
                <TabsTrigger value="rag">RAG Nodes</TabsTrigger>
              </TabsList>

              <TabsContent value="progress" className="flex flex-col gap-1">
                {progress?.stepNames.map((stepName, index) => (
                  <div
                    key={index}
                    className={cn(
                      "flex flex-row items-center gap-4 rounded-md p-4",
                      index < progress.currentStep
                        ? "bg-background-3"
                        : index === progress.currentStep
                          ? "bg-accent-secondary-2"
                          : "bg-background-8",
                    )}
                  >
                    <div>
                      <FontAwesomeIcon
                        icon={
                          index < progress.currentStep ||
                          (index === progress.steps - 1 &&
                            progress.isParsing === false)
                            ? faCheck
                            : index === progress.currentStep
                              ? faRefresh
                              : faCircle
                        }
                        className={cn({
                          "animate-spin":
                            index === progress.currentStep &&
                            progress.isParsing,
                        })}
                      />
                    </div>
                    <div className="font-bold">{stepName}</div>
                    {index === progress.currentStep && progress.isParsing && (
                      <div className="text-sm italic">(In Progress)</div>
                    )}
                    {stepName === "markdown_and_nodes" &&
                      progress.nodes > 0 && (
                        <div className="text-muted-foreground text-sm">
                          {progress.nodes} nodes found
                        </div>
                      )}
                    {stepName === "node_summary" &&
                      progress.nodesToSummarize > 0 && (
                        <div className="text-muted-foreground text-sm">
                          {progress.nodesSummarized} /{" "}
                          {progress.nodesToSummarize} summarized
                        </div>
                      )}
                    {stepName === "node_embedding" &&
                      progress.nodesToEmbed > 0 && (
                        <div className="text-muted-foreground text-sm">
                          {progress.nodesEmbedded} / {progress.nodesToEmbed}{" "}
                          embedded
                        </div>
                      )}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="markdown" className="flex flex-col gap-4">
                <div className="markdown bg-background p-4">
                  <Markdown allowRawHtml>{markdown || "N/A"}</Markdown>
                </div>
              </TabsContent>

              <TabsContent value="rag" className="flex flex-col gap-8">
                <div className="flex max-h-[40vh] flex-col gap-0">
                  {extendedRagSummary.map((node, index) => (
                    <div
                      key={index}
                      className={cn(
                        "flex flex-col gap-2 p-4",
                        LEVEL_CLASSNAMES.container[node.level],
                      )}
                    >
                      <div className="flex flex-row gap-4">
                        <div className="flex flex-1/2 flex-col gap-4 text-xs">
                          <div
                            className={cn(
                              LEVEL_CLASSNAMES.title[node.level],
                              "font-bold",
                            )}
                          >
                            # {index + 1} - {node.fileNode.title}
                          </div>
                          <div className="markdown text-base">
                            <Markdown allowRawHtml>
                              {node.fileNode.summary}
                            </Markdown>
                          </div>
                          <div className="font-bold">Vector DB Payloads:</div>
                          <ul className="flex list-disc flex-col gap-2 pl-4">
                            {node.payloads.map((payload, payloadIndex) => (
                              <li key={payloadIndex} className="text-xs">
                                {JSON.stringify(payload)}
                              </li>
                            ))}
                          </ul>
                          <div className="flex flex-row gap-2">
                            <div className={LABEL_CLASSNAME}>UUID:</div>{" "}
                            {node.fileNode.uuid}
                          </div>
                          <div className="flex flex-row gap-2">
                            <div className={LABEL_CLASSNAME}>Level:</div>{" "}
                            {node.level}
                          </div>
                          <div className="flex flex-row gap-2">
                            <div className={LABEL_CLASSNAME}>Pages:</div>{" "}
                            {node.fileNode.startPage}
                            {node.fileNode.startPage !==
                              node.fileNode.endPage &&
                              ` - ${node.fileNode.endPage}`}
                          </div>
                          <div className="flex flex-row gap-2">
                            <div className={LABEL_CLASSNAME}>Indexes:</div>{" "}
                            {node.fileNode.startIndex} -{" "}
                            {node.fileNode.endIndex} &nbsp; (
                            {node.fileNode.length})
                          </div>
                          <div className="flex flex-row gap-2">
                            <div className={LABEL_CLASSNAME}>Node:</div>{" "}
                            <div className="flex flex-row gap-2">
                              <div>
                                {node.fileNode.isLeaf ? "Is" : "Not"} leaf
                              </div>
                              <div>
                                {node.fileNode.isChunk ? "Is" : "Not"} chunk
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="markdown max-h-120 flex-1/2 overflow-y-auto">
                          <Markdown allowRawHtml>{node.markdown}</Markdown>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </PageBody>
      </Page>
    </>
  );
};
